import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { AppLayout, PageHeader } from '@/components/layout'
import { usePermissions, type Permission } from '@/hooks/api'
import { useIdentity } from '@/hooks/api/useIdentity'
import { canAccessWidgetHub } from '@/hooks/api/usePermissions'
import { resolveAdminForceUrl } from '@/lib/adminForceUrl'
import { RootLayout } from '@/App'

// Auth pages
import {
  LoginPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  ActivateAccountPage,
  TwoFactorPage,
  OAuthAuthorizePage,
} from '@/pages/auth'

// Error pages
import { NotFoundPage, ForbiddenPage, ThrowTestPage } from '@/pages/errors'
import { RouteErrorElement } from '@/components/common'

// Dashboard
import { DashboardPage } from '@/pages/dashboard'

// Notes
import { NotesPage } from '@/pages/notes'

// Conversations
import { ConversationsPage, ConversationRedirectPage } from '@/pages/conversations'

// Assistants
import { AssistantsPage, AssistantEditPage, AssistantCreatePage, TryAssistantPage } from '@/pages/assistants'

// Widget hub
import { WidgetsPage, WidgetTemplateCreatePage, WidgetTemplateEditPage } from '@/pages/widgets'

// Profile
import { ProfilePage } from '@/pages/profile'

// Onboarding
import { OnboardingPage } from '@/pages/onboarding'

// Self-service Signup
import { SignupPage } from '@/pages/signup'
// Simulations
import { SimulationsPage, SimRunDetailsPage, SimBatchDetailPage } from '@/pages/simulations'

// Connections

// Prompt Templates
import {
  PromptTemplatesPage,
  PromptTemplateEditPage,
  PromptTemplateCreatePage,
} from '@/pages/prompt-templates'

// Users
import { UsersPage, UserEditPage, UserCreatePage, UserInvitePage } from '@/pages/users'

// Phone Numbers (hidden admin page)
import { PhoneNumbersPage } from '@/pages/phone-numbers'

// Voice Admin (hidden admin page)
import { VoiceAdminPage } from '@/pages/admin/voices'

// Customers
import { CustomersPage, CustomerEditPage, CustomerCreatePage } from '@/pages/customers'

// Signups (Leads)
import { SignupsPage, SignupDetailPage } from '@/pages/signups'

// Partners
import { PartnersPage, PartnerEditPage, PartnerCreatePage } from '@/pages/partners'

// Placeholder page for development - will be replaced with actual pages
function PlaceholderPage({ titleKey, subtitleKey }: { titleKey: string; subtitleKey?: string }) {
  const { t } = useTranslation()
  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t(titleKey)}
        subtitle={subtitleKey ? t(subtitleKey) : t('pages.placeholder.under_construction')}
      />
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-6xl">🚧</div>
          <p className="text-gray-500 dark:text-gray-400">
            {t('pages.placeholder.coming_soon')}
          </p>
        </div>
      </div>
    </div>
  )
}

// Loading spinner component
function LoadingSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-cream dark:bg-gray-950">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
    </div>
  )
}

// Protected route wrapper
function ProtectedRoute({ 
  children,
  requiredPermission,
  accessCheck,
}: { 
  children: React.ReactNode
  requiredPermission?: Permission
  accessCheck?: (context: {
    role: string
    customerId?: number
    partnerId?: number
    customerMeta?: Record<string, unknown>
  }) => boolean
}) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const isLoading = useAuthStore((state) => state.isLoading)
  const authUser = useAuthStore((state) => state.user)
  const checkAuth = useAuthStore((state) => state.checkAuth)
  const { can, role } = usePermissions()
  const { user: identityUser, isLoading: identityLoading } = useIdentity()
  // Subscribing to the location re-evaluates the forced URL on every navigation
  const location = useLocation()

  // Check auth on mount
  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  const adminForceUrl =
    isAuthenticated && !isLoading
      ? resolveAdminForceUrl({
          forceUrl: import.meta.env.VITE_ADMIN_FORCE_URL,
          role: authUser?.role,
          location: { ...location, origin: window.location.origin },
        })
      : null

  useEffect(() => {
    if (adminForceUrl) {
      window.location.replace(adminForceUrl)
    }
  }, [adminForceUrl])

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (!isAuthenticated) {
    // Store intended destination for redirect after login
    const currentPath = window.location.pathname
    if (currentPath !== '/login' && currentPath !== '/') {
      sessionStorage.setItem('redirect_after_login', currentPath)
    }
    return <Navigate to="/login" replace />
  }

  if (adminForceUrl) {
    return <LoadingSpinner />
  }

  // Determine access: role-based permission OR custom access check
  const hasRolePermission = !requiredPermission || can(requiredPermission)

  if (accessCheck) {
    if (hasRolePermission) {
      return <>{children}</>
    }
    // Role check failed — fall back to identity-based access check
    if (identityLoading) {
      return <LoadingSpinner />
    }
    const customerId =
      identityUser?.customer_id ?? (authUser?.customerId ? Number(authUser.customerId) : undefined)
    const partnerId =
      identityUser?.partner_id ?? (authUser?.partnerId ? Number(authUser.partnerId) : undefined)
    const customerMeta = identityUser?.customer?.meta
    if (accessCheck({ role, customerId, partnerId, customerMeta })) {
      return <>{children}</>
    }
    return <Navigate to="/403" replace />
  }

  if (!hasRolePermission) {
    return <Navigate to="/403" replace />
  }

  return <>{children}</>
}

// Custom access check for prompt templates: admin, partner, or user with allowed_to_make_templates
const promptTemplateAccessCheck = ({ role, customerMeta }: { role: string; customerMeta?: Record<string, unknown> }) => {
  if (role === 'admin' || role === 'partner') return true
  if (role === 'user' && customerMeta?.allowed_to_make_templates === true) return true
  return false
}

const widgetHubAccessCheck = ({
  role,
  customerId,
  partnerId,
}: {
  role: string
  customerId?: number
  partnerId?: number
}) => canAccessWidgetHub({ role, customerId, partnerId })

// Public route wrapper (redirects to dashboard if already authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const isLoading = useAuthStore((state) => state.isLoading)
  const requires2FA = useAuthStore((state) => state.requires2FA)

  if (isLoading) {
    return <LoadingSpinner />
  }

  // Allow access to 2FA page when 2FA is required
  if (requires2FA) {
    return <>{children}</>
  }

  if (isAuthenticated) {
    // Check for redirect after login
    const redirectPath = sessionStorage.getItem('redirect_after_login')
    if (redirectPath) {
      sessionStorage.removeItem('redirect_after_login')
      return <Navigate to={redirectPath} replace />
    }
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

// 2FA route - requires temporary token but not full auth
function TwoFactorRoute({ children }: { children: React.ReactNode }) {
  const requires2FA = useAuthStore((state) => state.requires2FA)
  const tempToken = useAuthStore((state) => state.tempToken)

  if (!requires2FA || !tempToken) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

// Create the data router with route configuration
export const router = createBrowserRouter(
  [
    {
      // Root layout wraps everything
      element: <RootLayout />,
      // Catches errors thrown anywhere in the route tree (loaders, actions,
      // render). Without this, React Router falls back to its built-in debug
      // page that exposes raw stack traces in production.
      errorElement: <RouteErrorElement />,
      children: [
        // Public Routes (Auth)
        {
          path: '/login',
          element: (
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          ),
        },
        {
          path: '/forgot-password',
          element: (
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          ),
        },
        {
          path: '/reset-password',
          element: (
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          ),
        },
        {
          path: '/activate',
          element: (
            <PublicRoute>
              <ActivateAccountPage />
            </PublicRoute>
          ),
        },
        {
          path: '/2fa',
          element: (
            <TwoFactorRoute>
              <TwoFactorPage />
            </TwoFactorRoute>
          ),
        },
        // Self-service Signup (Public)
        {
          path: '/signup/:stepKey?',
          element: <SignupPage />,
        },
        // Payment link (Public) — short, opaque, and the whole URL a customer gets
        {
          path: '/p/:paymentToken',
          element: <SignupPage />,
        },
        // OAuth authorize (standalone -- no PublicRoute/ProtectedRoute wrapper)
        {
          path: '/oauth/authorize',
          element: <OAuthAuthorizePage />,
        },
        // Error pages
        { path: '/403', element: <ForbiddenPage /> },
        { path: '/404', element: <NotFoundPage /> },

        // Hidden crash test route — DEV ONLY. In production this branch is
        // statically eliminated by Vite (import.meta.env.DEV is replaced with
        // a literal `false`), so the route doesn't exist and visitors fall
        // through to the 404 catch-all. This prevents anyone or any crawler
        // from spamming production Sentry by hitting /__throw.
        ...(import.meta.env.DEV
          ? [{ path: '/__throw', element: <ThrowTestPage /> }]
          : []),

        // Protected Routes with AppLayout
        {
          path: '/',
          element: (
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          ),
          children: [
            // Dashboard
            { index: true, element: <Navigate to="/dashboard" replace /> },
            { path: 'dashboard', element: <DashboardPage /> },

            // Core features
            { path: 'notes', element: <NotesPage /> },
            { path: 'conversations', element: <ConversationsPage /> },
            { path: 'conversations/:id', element: <Navigate to="overview" replace /> },
            { path: 'conversations/:id/:tab', element: <ConversationsPage /> },
            { path: 'conversations/redirect/:twilioId', element: <ConversationRedirectPage /> },
            { path: 'assistants', element: <AssistantsPage /> },
            {
              path: 'assistants/:id',
              element: (
                <ProtectedRoute requiredPermission="edit:assistants">
                  <Navigate to="about" replace />
                </ProtectedRoute>
              ),
            },
            { path: 'assistants/:id/try', element: <TryAssistantPage /> },
            { path: 'assistants/:id/try/conversation/:conversationId', element: <TryAssistantPage /> },
            {
              path: 'assistants/:id/:tab',
              element: (
                <ProtectedRoute requiredPermission="edit:assistants">
                  <AssistantEditPage />
                </ProtectedRoute>
              ),
            },
            // Clone routes (must come before /assistants/create)
            {
              path: 'assistants/create/clone/:originalId',
              element: (
                <ProtectedRoute requiredPermission="create:assistants">
                  <Navigate to="about" replace />
                </ProtectedRoute>
              ),
            },
            {
              path: 'assistants/create/clone/:originalId/:tab',
              element: (
                <ProtectedRoute requiredPermission="create:assistants">
                  <AssistantCreatePage />
                </ProtectedRoute>
              ),
            },
            // Create routes
            {
              path: 'assistants/create',
              element: (
                <ProtectedRoute requiredPermission="create:assistants">
                  <Navigate to="about" replace />
                </ProtectedRoute>
              ),
            },
            {
              path: 'assistants/create/:tab',
              element: (
                <ProtectedRoute requiredPermission="create:assistants">
                  <AssistantCreatePage />
                </ProtectedRoute>
              ),
            },

            // Widget hub
            {
              path: 'widgets',
              element: (
                <ProtectedRoute requiredPermission="view:widgets" accessCheck={widgetHubAccessCheck}>
                  <WidgetsPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'widgets/templates/new',
              element: (
                <ProtectedRoute requiredPermission="view:widgets" accessCheck={widgetHubAccessCheck}>
                  <WidgetTemplateCreatePage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'widgets/templates/:id',
              element: (
                <ProtectedRoute requiredPermission="view:widgets" accessCheck={widgetHubAccessCheck}>
                  <WidgetTemplateEditPage />
                </ProtectedRoute>
              ),
            },

            // Prompt templates
            {
              path: 'prompt-templates',
              element: (
                <ProtectedRoute requiredPermission="view:prompt-templates" accessCheck={promptTemplateAccessCheck}>
                  <PromptTemplatesPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'prompt-templates/create',
              element: (
                <ProtectedRoute requiredPermission="view:prompt-templates" accessCheck={promptTemplateAccessCheck}>
                  <PromptTemplateCreatePage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'prompt-templates/:id',
              element: (
                <ProtectedRoute requiredPermission="view:prompt-templates" accessCheck={promptTemplateAccessCheck}>
                  <PromptTemplateEditPage />
                </ProtectedRoute>
              ),
            },

            // Partner tools
            {
              path: 'partners',
              element: (
                <ProtectedRoute requiredPermission="view:partners">
                  <PartnersPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'partners/create',
              element: (
                <ProtectedRoute requiredPermission="view:partners">
                  <PartnerCreatePage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'partners/:id',
              element: (
                <ProtectedRoute requiredPermission="view:partners">
                  <PartnerEditPage />
                </ProtectedRoute>
              ),
            },
            // Partner Users - nested under partners
            {
              path: 'partners/:partnerId/users',
              element: (
                <ProtectedRoute requiredPermission="view:partners">
                  <UsersPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'partners/:partnerId/users/create',
              element: (
                <ProtectedRoute requiredPermission="view:partners">
                  <UserCreatePage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'partners/:partnerId/users/invite',
              element: (
                <ProtectedRoute requiredPermission="view:partners">
                  <UserInvitePage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'partners/:partnerId/users/:id',
              element: (
                <ProtectedRoute requiredPermission="view:partners">
                  <UserEditPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'customers',
              element: (
                <ProtectedRoute requiredPermission="view:customers">
                  <CustomersPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'customers/create',
              element: (
                <ProtectedRoute requiredPermission="view:customers">
                  <CustomerCreatePage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'customers/:id',
              element: (
                <ProtectedRoute requiredPermission="view:customers">
                  <CustomerEditPage />
                </ProtectedRoute>
              ),
            },
            // Customer Users - nested under customers
            {
              path: 'customers/:customerId/users',
              element: (
                <ProtectedRoute requiredPermission="view:customers">
                  <UsersPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'customers/:customerId/users/create',
              element: (
                <ProtectedRoute requiredPermission="view:customers">
                  <UserCreatePage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'customers/:customerId/users/invite',
              element: (
                <ProtectedRoute requiredPermission="view:customers">
                  <UserInvitePage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'customers/:customerId/users/:id',
              element: (
                <ProtectedRoute requiredPermission="view:customers">
                  <UserEditPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'signups',
              element: (
                <ProtectedRoute requiredPermission="view:customers">
                  <SignupsPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'signups/:id',
              element: (
                <ProtectedRoute requiredPermission="view:customers">
                  <SignupDetailPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'onboarding',
              element: (
                <ProtectedRoute requiredPermission="view:onboarding">
                  <OnboardingPage />
                </ProtectedRoute>
              ),
            },

            // Simulations
            {
              path: 'simulations',
              element: (
                <ProtectedRoute requiredPermission="view:simulations">
                  <Navigate to="batches" replace />
                </ProtectedRoute>
              ),
            },
            {
              path: 'simulations/:tab',
              element: (
                <ProtectedRoute requiredPermission="view:simulations">
                  <SimulationsPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'simulations/batches/:id',
              element: (
                <ProtectedRoute requiredPermission="view:simulations">
                  <SimBatchDetailPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'simulations/runs/:id',
              element: (
                <ProtectedRoute requiredPermission="view:simulations">
                  <SimRunDetailsPage />
                </ProtectedRoute>
              ),
            },

            // Phone numbers (hidden admin page - not in sidebar)
            {
              path: 'phone-numbers',
              element: (
                <ProtectedRoute requiredPermission="access:admin-panel">
                  <PhoneNumbersPage />
                </ProtectedRoute>
              ),
            },

            // Voice admin (admin-only, listed under partner tools in the sidebar)
            {
              path: 'admin/voices',
              element: (
                <ProtectedRoute requiredPermission="access:admin-panel">
                  <VoiceAdminPage />
                </ProtectedRoute>
              ),
            },

            // User management (standalone)
            { path: 'users', element: <UsersPage /> },
            { path: 'users/create', element: <UserCreatePage /> },
            { path: 'users/invite', element: <UserInvitePage /> },
            { path: 'users/:id', element: <UserEditPage /> },

            // Profile
            { path: 'profile', element: <ProfilePage /> },
          ],
        },

        // 404 - catch all
        { path: '*', element: <NotFoundPage /> },
      ],
    },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    },
  }
)
